import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Loader2, Save, Plus, Trash2, Sparkles, Palette, Send } from 'lucide-react';

type SectionType = {
  id: string;
  emoji: string;
  title: string;
  body: string;
};

type ThemeCategory = {
  id: string;
  label: string;
  images: string[];
};

type DesignerConfig = {
  channelId: string;
  themeCategory: string;
  themeImageUrl: string;
  accentColor: string;
  introTitle: string;
  introBody: string;
  sections: SectionType[];
  inviteLink: string;
};

const THEME_CATEGORIES: ThemeCategory[] = [
  {
    id: 'anime',
    label: 'Anime / Manga',
    images: [
      'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1520375300877-3500e7adf613?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1535223289827-42f1e9919769?auto=format&fit=crop&w=900&q=80',
    ],
  },
  {
    id: 'gaming',
    label: 'Gaming',
    images: [
      'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1553481187-be93c21490a9?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1560253023-3ec5d502959f?auto=format&fit=crop&w=900&q=80',
    ],
  },
  {
    id: 'chill',
    label: 'Lo-Fi / Chill Vibes',
    images: [
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1470246973918-29a93221c455?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1495567720989-cebdbdd97913?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1493244040629-496f6d136cc3?auto=format&fit=crop&w=900&q=80',
    ],
  },
  {
    id: 'neon',
    label: 'Glitch / Neon',
    images: [
      'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1550686041-366ad85a1355?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1519751138087-5bf79df62d5b?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1490806843957-31f4c9a91c65?auto=format&fit=crop&w=900&q=80',
    ],
  },
];

const defaultSections: SectionType[] = [
  {
    id: crypto.randomUUID(),
    emoji: '👋',
    title: 'Presentati alla community',
    body: 'Condividi qualcosa su di te, i tuoi interessi e cosa ti ha portato qui.',
  },
  {
    id: crypto.randomUUID(),
    emoji: '📜',
    title: 'Leggi il regolamento',
    body: 'Prima di iniziare, leggi le regole del server per mantenere un ambiente sereno e rispettoso.',
  },
  {
    id: crypto.randomUUID(),
    emoji: '🛡️',
    title: 'Contatta i moderatori',
    body: 'Se riscontri problemi, tagga lo staff nel canale dedicato. Evita DM privati ai membri dello staff.',
  },
];

const initialConfig: DesignerConfig = {
  channelId: '',
  themeCategory: THEME_CATEGORIES[0].id,
  themeImageUrl: THEME_CATEGORIES[0].images[0],
  accentColor: '#ff4d8d',
  introTitle: 'Benvenuto su {server.name}',
  introBody: 'Ciao e un caloroso benvenuto a tutti i nuovi membri. Siamo felici di averti con noi.',
  sections: defaultSections,
  inviteLink: 'https://discord.gg/your-invite',
};

export function WelcomeDesignerPlugin({ serverId }: { serverId: string }) {
  const { data: plugins, isLoading: pluginsLoading } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const welcomeDesignerPlugin = (plugins as any[])?.find((p: any) => p.pluginId === 'welcome-designer');
  const isEnabled = welcomeDesignerPlugin?.enabled ?? false;

  const [config, setConfig] = useState<DesignerConfig>(initialConfig);

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'welcome-designer') : pluginApi.disablePlugin(serverId, 'welcome-designer'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (payload: DesignerConfig) => pluginApi.updatePluginConfig(serverId, 'welcome-designer', payload),
    {
      onSuccess: () => toast.success('Welcome Designer salvato con successo'),
      onError: (err: any) => toast.error(err?.message || 'Errore durante il salvataggio'),
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  const { mutate: spawnWelcome, isPending: spawning } = useApiMutation(
    () => pluginApi.spawnWelcomeDesigner(serverId),
    {
      onSuccess: () => toast.success('Messaggio Welcome pubblicato nel canale selezionato'),
      onError: (err: any) => toast.error(err?.message || 'Impossibile pubblicare il messaggio Welcome'),
    }
  );

  useEffect(() => {
    if (!welcomeDesignerPlugin?.config) return;
    const merged = {
      ...initialConfig,
      ...welcomeDesignerPlugin.config,
      sections: Array.isArray(welcomeDesignerPlugin.config.sections) && welcomeDesignerPlugin.config.sections.length > 0
        ? welcomeDesignerPlugin.config.sections
        : defaultSections,
    } as DesignerConfig;
    setConfig(merged);
  }, [welcomeDesignerPlugin]);

  const selectedCategory = useMemo(
    () => THEME_CATEGORIES.find((cat) => cat.id === config.themeCategory) || THEME_CATEGORIES[0],
    [config.themeCategory]
  );

  const handleThemeCategoryChange = (categoryId: string) => {
    const category = THEME_CATEGORIES.find((item) => item.id === categoryId) || THEME_CATEGORIES[0];
    setConfig((prev) => ({
      ...prev,
      themeCategory: category.id,
      themeImageUrl: category.images[0],
    }));
  };

  const addSection = () => {
    setConfig((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          id: crypto.randomUUID(),
          emoji: '✨',
          title: 'Nuova sezione',
          body: 'Descrivi qui il contenuto di questa sezione.',
        },
      ],
    }));
  };

  const removeSection = (id: string) => {
    setConfig((prev) => ({
      ...prev,
      sections: prev.sections.filter((section) => section.id !== id),
    }));
  };

  const updateSection = (id: string, patch: Partial<SectionType>) => {
    setConfig((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => (section.id === id ? { ...section, ...patch } : section)),
    }));
  };

  if (pluginsLoading || !welcomeDesignerPlugin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-pink-500/20">
            <Sparkles className="w-6 h-6 text-pink-300" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Welcome Designer</h3>
            <p className="text-sm text-muted-foreground">
              Builder visuale per creare messaggi di benvenuto strutturati in stile card.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="welcome-designer-toggle" className="cursor-pointer">Plugin attivo</Label>
          <Switch
            id="welcome-designer-toggle"
            checked={isEnabled}
            onCheckedChange={(checked) => togglePlugin({ enabled: checked })}
          />
        </div>
      </div>

      <div className={cn('grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-6', !isEnabled && 'opacity-75')}>
        <div className="space-y-6">
          <section className="glass-card p-6 space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Configurazione Base
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Canale di invio</Label>
                <Select
                  value={config.channelId || 'none'}
                  onValueChange={(value) => setConfig((prev) => ({ ...prev, channelId: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Colore accento</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.accentColor}
                    onChange={(e) => setConfig((prev) => ({ ...prev, accentColor: e.target.value }))}
                    className="w-14 h-10 p-1"
                  />
                  <Input
                    value={config.accentColor}
                    onChange={(e) => setConfig((prev) => ({ ...prev, accentColor: e.target.value }))}
                    placeholder="#ff4d8d"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Titolo intro</Label>
              <Input
                value={config.introTitle}
                onChange={(e) => setConfig((prev) => ({ ...prev, introTitle: e.target.value }))}
                placeholder="Benvenuto su {server.name}"
              />
            </div>

            <div className="space-y-2">
              <Label>Testo intro</Label>
              <Textarea
                rows={3}
                value={config.introBody}
                onChange={(e) => setConfig((prev) => ({ ...prev, introBody: e.target.value }))}
                placeholder="Messaggio introduttivo"
              />
            </div>

            <div className="space-y-2">
              <Label>Invite link</Label>
              <Input
                value={config.inviteLink}
                onChange={(e) => setConfig((prev) => ({ ...prev, inviteLink: e.target.value }))}
                placeholder="https://discord.gg/..."
              />
            </div>
          </section>

          <section className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Sezioni Messaggio</h4>
              <Button onClick={addSection} size="sm" variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Aggiungi sezione
              </Button>
            </div>

            <div className="space-y-4">
              {config.sections.map((section, index) => (
                <div key={section.id} className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Sezione {index + 1}</p>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => removeSection(section.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[90px_1fr] gap-3">
                    <div className="space-y-1">
                      <Label>Emoji</Label>
                      <Input
                        value={section.emoji}
                        onChange={(e) => updateSection(section.id, { emoji: e.target.value })}
                        maxLength={4}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Titolo</Label>
                      <Input
                        value={section.title}
                        onChange={(e) => updateSection(section.id, { title: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label>Contenuto</Label>
                    <Textarea
                      rows={3}
                      value={section.body}
                      onChange={(e) => updateSection(section.id, { body: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="glass-card p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Tema</h4>
              <Select value={config.themeCategory} onValueChange={handleThemeCategoryChange}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Categoria tema" />
                </SelectTrigger>
                <SelectContent>
                  {THEME_CATEGORIES.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto pr-1">
              {selectedCategory.images.map((image) => (
                <button
                  key={image}
                  className={cn(
                    'rounded-lg border-2 overflow-hidden transition-all',
                    config.themeImageUrl === image ? 'border-primary scale-[1.02]' : 'border-transparent hover:border-primary/50'
                  )}
                  onClick={() => setConfig((prev) => ({ ...prev, themeImageUrl: image }))}
                  type="button"
                >
                  <img src={image} alt="theme" className="w-full h-20 object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </section>

          <div className="flex flex-col justify-end gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:flex-row">
            <p className="mr-auto text-xs text-muted-foreground sm:max-w-sm sm:self-center">
              Salva le modifiche, poi pubblica manualmente il messaggio nel canale scelto. Il bot non invierà messaggi quando entra un nuovo utente.
            </p>
            <Button onClick={() => updateConfig(config)} disabled={saving || spawning} variant="outline" className="gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <Save className="w-4 h-4" />
              Salva configurazione
            </Button>
            <Button onClick={() => spawnWelcome()} disabled={saving || spawning || !config.channelId} className="gap-2">
              {spawning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Pubblica nel canale
            </Button>
          </div>
        </div>

        <div className="space-y-4 xl:sticky xl:top-20 self-start">
          <div className="text-sm uppercase tracking-wide text-muted-foreground">Anteprima</div>

          <div className="rounded-2xl border border-white/10 bg-[#1e2230] overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-white/10 bg-[#151826]">
              <p className="font-semibold" style={{ color: config.accentColor }}>{config.introTitle || 'Benvenuto su {server.name}'}</p>
              <p className="text-sm text-slate-300 mt-2 whitespace-pre-line">{config.introBody || 'Messaggio introduttivo.'}</p>
            </div>

            <img
              src={config.themeImageUrl}
              alt="welcome theme"
              className="w-full h-36 object-cover"
            />

            <div className="p-4 space-y-3 bg-[#151826]">
              {config.sections.map((section) => (
                <div key={section.id} className="rounded-lg border border-white/10 bg-[#11131f] p-3">
                  <p className="text-sm font-semibold" style={{ color: config.accentColor }}>
                    {section.emoji} {section.title}
                  </p>
                  <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{section.body}</p>
                </div>
              ))}

              <div className="rounded-lg border border-white/10 bg-[#11131f] p-3">
                <p className="text-sm font-semibold" style={{ color: config.accentColor }}>🔗 Invite link</p>
                <p className="text-sm text-blue-300 mt-1 break-all">{config.inviteLink}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
